<!-- NotificationModal.vue -->
<template>
  <div class="notifications-container" ref="containerRef">

    <LoadingOverlay v-if="store.isLoading && items.length === 0" :show="true" />

    <template v-else-if="items.length > 0">

      <!-- Список -->
      <div class="divide-y divide-gray-100 dark:divide-gray-700/50">
        <div
            v-for="n in items"
            :key="n.id"
            class="notification-item group relative flex gap-3 px-4 py-3 transition-colors cursor-pointer"
            :class="n.is_read
              ? 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
              : 'bg-indigo-50/60 dark:bg-indigo-950/20 hover:bg-indigo-100/60 dark:hover:bg-indigo-950/30'"
            @click="handleItemClick(n)"
        >
          <!-- Полоска слева для непрочитанных -->
          <div
              v-if="!n.is_read"
              class="absolute left-0 top-2 bottom-2 w-0.5 rounded-r-full"
              :class="getKindConfig(n.kind).bar"
          />

          <!-- Иконка -->
          <div
              class="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-base select-none mt-0.5"
              :class="getKindConfig(n.kind).bg"
          >{{ getKindConfig(n.kind).emoji }}</div>

          <!-- Текст -->
          <div class="flex-1 min-w-0 pr-14">
            <div class="flex items-baseline justify-between gap-2">
              <p
                  class="text-sm leading-snug"
                  :class="n.is_read ? 'font-normal text-gray-700 dark:text-gray-300' : 'font-semibold text-gray-900 dark:text-gray-100'"
              >{{ n.title }}</p>
              <span class="text-[11px] text-gray-400 dark:text-gray-500 whitespace-nowrap flex-shrink-0">
                {{ formatRelativeTime(n.created_at) }}
              </span>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 leading-relaxed">
              {{ n.description }}
            </p>
            <a
                v-if="resolveNotificationTarget(n)"
                @click.stop="handleNavigate(n)"
                class="inline-flex items-center mt-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
            >{{ resolveTargetLabel(n) }}</a>
          </div>

          <!-- Кнопки действий (hover) -->
          <div class="absolute right-3 top-1/2 -translate-y-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
                @click.stop="deleteItem(n)"
                class="w-6 h-6 flex items-center justify-center rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition text-xs shadow-sm"
                title="Удалить"
            >×</button>
          </div>
        </div>
      </div>

      <!-- Подгрузка -->
      <div v-if="loadingMore" class="flex justify-center py-3">
        <LoadingSpinner class="w-5 h-5 text-indigo-500" />
      </div>

      <!-- Ссылка на все -->
      <div class="px-4 py-3 border-t border-gray-100 dark:border-gray-700 mt-1">
        <router-link
            :to="{ name: 'UserNotificationPage' }"
            @click="$emit('close')"
            class="block text-center text-sm text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
        >
          Все уведомления →
        </router-link>
      </div>
    </template>

    <!-- Пустое состояние -->
    <div v-else class="flex flex-col items-center py-12 text-gray-400 dark:text-gray-500">
      <span class="text-4xl mb-3 opacity-60">🔔</span>
      <p class="text-sm">Уведомлений пока нет</p>
    </div>

  </div>
</template>

<script>
import {ref, computed, onMounted, onUnmounted} from 'vue';
import {useRouter} from 'vue-router';
import {debounce} from 'lodash-es';
import LoadingOverlay from '@/core/components/material/v1/LoadingOverlay.vue';
import LoadingSpinner from '@/core/components/material/v1/LoadingSpinner.vue';
import {useUserNotificationStore} from '@/modules/user_notification/store/userNotificationStore.js';
import {getKindConfig, formatRelativeTime, resolveNotificationTarget, resolveTargetLabel} from '@/core/utils/notificationUtils.js';

export default {
    name: 'NotificationModal',
    components: {LoadingOverlay, LoadingSpinner},
    emits: ['close'],
    setup(_, {emit}) {
        const store = useUserNotificationStore();
        const router = useRouter();
        const containerRef = ref(null);
        const loadingMore = ref(false);

        // Локальный накопительный список — не зависит от store.rows
        const items = ref([]);
        const page = ref(1);
        const hasMore = ref(true);

        const hasUnread = computed(() => items.value.some(n => !n.is_read));

        async function loadPage(p) {
            if (p === 1) {
                await store.fetchData({page: 1});
                items.value = [...store.rows];
            } else {
                loadingMore.value = true;
                try {
                    await store.fetchData({page: p});
                    items.value = [...items.value, ...store.rows];
                } finally {
                    loadingMore.value = false;
                }
            }
            hasMore.value = store.currentPage < store.totalPages;
            page.value = store.currentPage;
        }

        async function markAsRead(n) {
            await store.markAsRead(n.id);
            const local = items.value.find(i => i.id === n.id);
            if (local) local.is_read = true;
        }

        async function deleteItem(n) {
            await store.deleteNotification(n.id);
            items.value = items.value.filter(i => i.id !== n.id);
        }

        async function markAllAsRead() {
            await store.markAllAsRead();
            items.value.forEach(n => { n.is_read = true; });
        }

        function handleNavigate(n) {
            if (!n.is_read) markAsRead(n);
            const target = resolveNotificationTarget(n);
            if (target) {
                router.push(target);
                emit('close');
            }
        }

        function handleItemClick(n) {
            handleNavigate(n);
        }

        const handleScroll = debounce(() => {
            if (loadingMore.value || !hasMore.value) return;
            const el = containerRef.value;
            if (!el) return;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight * 0.85) {
                loadPage(page.value + 1);
            }
        }, 150);

        onMounted(() => {
            loadPage(1);
            containerRef.value?.addEventListener('scroll', handleScroll);
        });

        onUnmounted(() => {
            containerRef.value?.removeEventListener('scroll', handleScroll);
        });

        function onAllMarkedRead() {
            items.value.forEach(n => { n.is_read = true; });
        }

        return {store, items, containerRef, loadingMore, hasUnread, getKindConfig, formatRelativeTime, resolveNotificationTarget, resolveTargetLabel, markAsRead, deleteItem, markAllAsRead, handleItemClick, handleNavigate, onAllMarkedRead};
    },
};
</script>

<style scoped>
.notifications-container {
  @apply max-h-[65vh] overflow-y-auto;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
